import React from 'react';
import _ from 'lodash';
import { Item } from 'semantic-ui-react';

import { StudentProfileEnum as StudentProfileFields } from 'Common/FieldEnum';
import { NutritionCodeEnum } from 'Common/Constants/Enumeration/NutritionCodeEnum';
import { ChargeByStatusModeEnum } from 'Common/Constants/Enumeration/ChargeByStatusModeEnum';
import {
  DEFAULT_GRADE_ID,
  GENERIC_ACCOUNT_IDS,
  NOTIFICATION_DIALOG_MESSAGES,
  NON_STUDENT_STATUSES,
  POS_ALACARTE_NOLIMIT,
} from 'Common/Constants';
import {
  SymbolIndicatorEnum,
  QueuedDirectionEnum,
  MEAL_TYPES_LABELS,
  ProhibitedItemFlags,
  StatusPriceMap,
  MealTypesEnum,
} from 'PointOfSale/modules/common/constants';
import { Formatters } from 'Common/Helpers';
import { multiply } from 'Common/Helpers/currency';
import { FALLBACK_IMAGE } from 'Common/Constants';
import { getSubtotal, getCost, getTax } from 'PointOfSale/helpers/transactions';

export const hasAllergens = student => {
  return Array.isArray(student.Allergens) && student.Allergens.length > 0;
};

export const hasSpecialMessage = student => {
  return student.Message && student.Message.length > 0 ? true : false;
};

export const getAllergensMessage = student => {
  if (hasAllergens(student)) {
    const studentName = `${student.LastName}, ${student.FirstName}`;
    return `${studentName} has the following allergens: ${student.Allergens.join(
      ', '
    )}`;
  }
  return null;
};

export const getAllergensMessageDialog = student => {
  if (hasAllergens(student)) {
    const studentName = `${student.LastName}, ${student.FirstName}`;

    const imageSrc = Boolean(student.Picture)
      ? Formatters.prependBase64(student.Picture)
      : FALLBACK_IMAGE;

    const message = (
      <Item.Group>
        <Item>
          <Item.Image size="small" src={imageSrc} />
          <Item.Content>
            <Item.Header>
              {studentName} has the following allergens:
            </Item.Header>
            <Item.Description>
              <p>{student.Allergens.join(', ')}</p>
            </Item.Description>
          </Item.Content>
        </Item>
      </Item.Group>
    );

    const dialog = {
      ...NOTIFICATION_DIALOG_MESSAGES.STUDENT_HAS_ALLERGENS,
      message,
    };
    return dialog;
  }
  return null;
};

export const getSpecialMessage = student => {
  if (hasSpecialMessage(student)) {
    const studentName = `${student.LastName}, ${student.FirstName}`;
    return `${studentName} has the following special message: ${student.Message}`;
  }
  return null;
};

export const getSpecialMessageDialog = student => {
  if (hasSpecialMessage(student)) {
    const studentName = `${student.LastName}, ${student.FirstName}`;

    const imageSrc = Boolean(student.Picture)
      ? Formatters.prependBase64(student.Picture)
      : FALLBACK_IMAGE;

    const message = (
      <Item.Group>
        <Item>
          <Item.Image size="small" src={imageSrc} />
          <Item.Content>
            <Item.Header>
              {studentName} has the following special message:
            </Item.Header>
            <Item.Description>
              <p>{student.Message}</p>
            </Item.Description>
          </Item.Content>
        </Item>
      </Item.Group>
    );

    const dialog = {
      ...NOTIFICATION_DIALOG_MESSAGES.SPECIAL_MESSAGE,
      message,
    };
    return dialog;
  }
  return null;
};

export const addAllergensProhibitedItems = (student, profile) => {
  const allergens = hasAllergens(student) ? student.Allergens : null;
  const specialMessage = hasSpecialMessage(student) ? student.Message : null;

  if (allergens) {
    profile.push({
      type: SymbolIndicatorEnum.Allergen,
      value: null,
      message: getAllergensMessage(student),
      isOpen: true,
    });
  }

  if (specialMessage) {
    profile.push({
      type: SymbolIndicatorEnum.SpecialMessage,
      value: null,
      message: getSpecialMessage(student),
      isOpen: true,
    });
  }
};

export const getIconAndColor = item => {
  if (!item || item === undefined) {
    return {};
  }
  switch (item.type) {
    case SymbolIndicatorEnum.ServingDirection:
      if (item.value === QueuedDirectionEnum.Right) {
        return {
          name: 'arrow alternate circle right outline',
          className: 'arrowSymbol',
        };
      } else {
        return {
          name: 'arrow alternate circle left outline',
          className: 'arrowSymbol',
        };
      }
    case SymbolIndicatorEnum.Allergen:
      return {
        name: 'warning sign',
        className: 'allergenSymbol',
      };
    case SymbolIndicatorEnum.InvalidStudent:
      return {
        name: 'warning circle',
        className: 'invalidStudentSymbol',
      };
    case SymbolIndicatorEnum.SpecialMessage:
      return {
        name: 'commenting',
        className: 'specialMessageSymbol',
      };
    case SymbolIndicatorEnum.ManuallyKeyed:
      return {
        name: 'table',
        className: 'manuallyKeyedSymbol',
      };
    case SymbolIndicatorEnum.VisitingStudent:
      return {
        name: 'bus',
        color: 'orange',
        className: 'visitingStudentSymbol',
      };
    default:
      return {};
  }
};

export const containsReimbursableMeal = itemsList =>
  itemsList.filter(i => i.NutritionCode === NutritionCodeEnum.FederalType1Meal)
    .length > 0;

export const containsReimbursableSnack = itemsList =>
  itemsList.filter(i => i.NutritionCode === NutritionCodeEnum.ReimbursableSnack)
    .length > 0;

export const getProhibitedFlags = (pricing, gradeId) => {
  const prices = pricing.filter(p => p.GradeId === gradeId);

  if (prices.length === 0) {
    return getProhibitedFlagsObj('');
  }

  const prohibitedFlags = prices[0].ProhibitedFlag;
  return getProhibitedFlagsObj(prohibitedFlags);
};

export const getProhibitedFlagsObj = prohibitedFlagString => {
  return {
    Free: prohibitedFlagString[0] === 'Y',
    Reduced: prohibitedFlagString[1] === 'Y',
    Student: prohibitedFlagString[2] === 'Y',
    Teacher: prohibitedFlagString[3] === 'Y',
    Adult: prohibitedFlagString[4] === 'Y',
    SFS: prohibitedFlagString[5] === 'Y',
    Catered: prohibitedFlagString[6] === 'Y',
  };
};

export const canStudentBuyItem = ({
  profile,
  item,
  currentSelectedMenu,
  prohibitedItems,
  boughtItems,
  pendingItems,
  taxRate,
  isOnline,
}) => {
  const notificationObj = getProhibitedMessage({
    profile,
    item,
    currentSelectedMenu,
    prohibitedItems,
    boughtItems,
    pendingItems,
    taxRate,
    isOnline,
  });

  return !Boolean(notificationObj);
};

/**
 * Given an item with { ItemId, NutritionCode, [Description], [ShortDescription]
 * }, return a copy of the given item that also has { StateReimbursable,
 * UnitPrice, Price, Qty, Converted, ConvertAmount }.
 * @param {Object} item
 * @param {Object} profile
 * @param {number} [quantity=1]
 * @param {string} [mealType]
 */
export const getItemWithData = (item, profile, quantity = 1, mealType) => {
  const studentGradeId = _.get(
    profile,
    StudentProfileFields.GradeId,
    DEFAULT_GRADE_ID
  );
  const statusCurrent = _.get(profile, StudentProfileFields.StatusCurrent);

  const pricingForGrade = getItemPricingForGrade(item, studentGradeId);

  const StateReimbursable = pricingForGrade.StateReimbursable;
  const { Converted, ConvertAmount } = getItemConvertAmountForGrade(
    item,
    studentGradeId
  );
  let UnitPrice = getPriceForStatus(pricingForGrade, statusCurrent);

  if (mealType === MealTypesEnum.Other && !_.isNil(item.UnitPrice)) {
    UnitPrice = item.UnitPrice || 0;
  }

  const Price = multiply(UnitPrice, item.Qty || quantity);

  const ret = {
    ItemId: item.ItemId,
    Description: item.Description,
    ShortDescription: item.ShortDescription,
    NutritionCode: item.NutritionCode,
    StateReimbursable,
    UnitPrice,
    Price,
    Qty: quantity,
    Converted,
    ConvertAmount,
  };

  return ret;
};

/**
 * Given an item, calculate the total price. If the item does not have data
 * attached, it will be looked up with the given profile first.
 * @param {Object} item
 * @param {Object} [profile]
 * @param {number} [quantity=1]
 * @param {number} [taxRate=0]
 */
export const getItemTotal = (item, taxRate, profile, quantity = 1) => {
  let itemWithData = item;

  if (_.isNil(itemWithData.Price)) {
    itemWithData = getItemWithData(item, profile, quantity);
  }

  const subTotal = itemWithData.Price;
  const total = getCost(subTotal, getTax(subTotal, taxRate));

  return total;
};

/**
 * Given an array of items, calculate the total price. If any of the items do
 * not have data attached, it will be looked up with the given profile first.
 * @param {Object} item
 * @param {Object} [profile]
 * @param {number} [quantity=1]
 * @param {number} [taxRate=0]
 */
export const getItemsTotal = (items, taxRate, profile) => {
  const itemsWithPrice = items.map(item => {
    if (_.isNil(item.Price)) {
      return getItemWithData(item, profile);
    }

    return item;
  });

  const subTotal = getSubtotal(itemsWithPrice);
  const total = getCost(subTotal, getTax(subTotal, taxRate));

  return total;
};

export const isReimbursableItem = item =>
  item ? item.NutritionCode === NutritionCodeEnum.FederalType1Meal : false;

export const isReimbursableSnack = item =>
  item ? item.NutritionCode === NutritionCodeEnum.ReimbursableSnack : false;

export const getChargeLimitNotification = ({
  pendingItems,
  taxRate,
  profile,
  amountPaid = 0,
}) => {
  // http://emsweb.mealsplus.com/development/mpWiki/Pages/POS_SiteSetupChargeorMealLimit.aspx
  // What counts as a charged meal?
  // Only transactions that include a reimbursable meals (Nut. Code = 1) that
  // cause their balance to go negative or more negative are counted as a
  // Charged Meal.
  // If a student has a 2.00 balance and the meal and other stuff cost 2.25 but
  // the meal was only 1.75 – This will count as a charged meal.
  // note that a student with a 2.00 balance that has a 2.25 transaction but
  // is also paying $1 in cash does not count as a charged meal, as the balance
  // would still be positive afterwards
  const pendingTotal = getItemsTotal(pendingItems, taxRate, profile);
  const pendingBalance = profile.Balance - pendingTotal + amountPaid;

  const hasReimbursableMeal = containsReimbursableMeal(pendingItems);

  if (pendingBalance < 0 && hasReimbursableMeal) {
    switch (profile[StudentProfileFields.ChargeByStatusMode]) {
      case ChargeByStatusModeEnum.MaxChargeAmount:
        const chargeAmountAllowed =
          profile[StudentProfileFields.CBSChargedAmtAllowed];

        if (
          !_.isNil(chargeAmountAllowed) &&
          pendingBalance < -chargeAmountAllowed
        ) {
          return NOTIFICATION_DIALOG_MESSAGES.CHARGED_MEAL_AMOUNT_LIMIT;
        }

        break;
      case ChargeByStatusModeEnum.MaxChargeCount:
        const chargeCountAllowed =
          profile[StudentProfileFields.CBSChargedMealCntAllowed];
        const chargedMealCount = profile[StudentProfileFields.ChargedMealCount];

        if (
          !_.isNil(chargeCountAllowed) &&
          chargedMealCount >= chargeCountAllowed
        ) {
          return NOTIFICATION_DIALOG_MESSAGES.CHARGED_MEAL_COUNT_LIMIT;
        }

        break;
      default:
        return null;
    }
  }

  return null;
};

export const getAlaCarteExceededNotification = ({
  pendingItems,
  profile,
  taxRate,
  isOnline = true,
}) => {
  //unable to check in offline mode - just allow all a la carte purchases
  if (!isOnline) {
    return null;
  }

  //prevent adding of items if it would be more than CanSpend
  if (!isGenericAccount(profile) && profile.CanSpend !== POS_ALACARTE_NOLIMIT) {
    //grab pending a la carte total
    const pendingAlaCarteItems = pendingItems.filter(
      item => !isReimbursableItem(item)
    );

    //note that it's possible for canSpend to be negative
    //so only check the pendingTotal if we're trying to buy an ala carte item
    if (pendingAlaCarteItems.length) {
      const pendingTotal = getItemsTotal(
        pendingAlaCarteItems,
        taxRate,
        profile
      );

      //compare to canSpend
      const canSpend = parseFloat(profile.CanSpend) || 0;

      if (pendingTotal > canSpend) {
        return NOTIFICATION_DIALOG_MESSAGES.ALACARTE_EXCEEDED;
      }
    }
  }

  return null;
};

export const getProhibitedMessage = ({
  profile,
  item,
  currentSelectedMenu,
  pendingItems,
  prohibitedItems = [],
  boughtItems = [],
  taxRate = 0,
  isOnline = true,
}) => {
  let notificationObj = null;
  const { StatusCurrent, Bought, BoughtReimbSnack, Id } = profile;
  const isGenericAccount = GENERIC_ACCOUNT_IDS.has(Id);
  const isExemptStatus = NON_STUDENT_STATUSES.includes(StatusCurrent);
  if (StatusCurrent) {
    const pricing = item.Pricing || [];
    const prohibitedFlags = getProhibitedFlags(pricing, profile.GradeId);
    switch (StatusCurrent) {
      case ProhibitedItemFlags.Free:
        if (prohibitedFlags.Free) {
          notificationObj = NOTIFICATION_DIALOG_MESSAGES.PROHIBITED_ITEMS;
        }
        break;
      case ProhibitedItemFlags.Reduced:
        if (prohibitedFlags.Reduced) {
          notificationObj = NOTIFICATION_DIALOG_MESSAGES.PROHIBITED_ITEMS;
        }
        break;
      case ProhibitedItemFlags.Paid:
      case ProhibitedItemFlags.Student:
        if (prohibitedFlags.Student) {
          notificationObj = NOTIFICATION_DIALOG_MESSAGES.PROHIBITED_ITEMS;
        }
        break;
      case ProhibitedItemFlags.Teacher:
        if (prohibitedFlags.Teacher) {
          notificationObj = NOTIFICATION_DIALOG_MESSAGES.PROHIBITED_ITEMS;
        }
        break;
      case ProhibitedItemFlags.Adult:
        if (prohibitedFlags.Adult) {
          notificationObj = NOTIFICATION_DIALOG_MESSAGES.PROHIBITED_ITEMS;
        }
        break;
      case ProhibitedItemFlags.SFS:
        if (prohibitedFlags.SFS) {
          notificationObj = NOTIFICATION_DIALOG_MESSAGES.PROHIBITED_ITEMS;
        }
        break;
      case ProhibitedItemFlags.Catered:
        if (prohibitedFlags.Catered) {
          notificationObj = NOTIFICATION_DIALOG_MESSAGES.PROHIBITED_ITEMS;
        }
        break;
      default:
        break;
    }
  }

  const isItemProhibited = prohibitedItems.includes(item.ItemId);

  const otherNotificationObj = getAlaCarteExceededNotification({
    pendingItems,
    taxRate,
    profile,
    isOnline,
  });

  if (isItemProhibited) {
    notificationObj = NOTIFICATION_DIALOG_MESSAGES.PROHIBITED_ITEMS;
  } else if (!isGenericAccount) {
    if (isExemptStatus) {
      notificationObj = otherNotificationObj;
    } else {
      switch (currentSelectedMenu) {
        case MEAL_TYPES_LABELS.Breakfast:
        case MEAL_TYPES_LABELS.Lunch:
        case MEAL_TYPES_LABELS.Supper:
          const numReimbursable = pendingItems.reduce(
            (sum, item) => sum + (isReimbursableItem(item) ? item.Qty : 0),
            0
          );

          if (otherNotificationObj) {
            notificationObj = otherNotificationObj;
          }

          if (Bought && numReimbursable) {
            notificationObj =
              NOTIFICATION_DIALOG_MESSAGES.REIMBURSABLE_MEAL_PURCHASED;
          } else if (numReimbursable > 1) {
            notificationObj =
              NOTIFICATION_DIALOG_MESSAGES.REIMBURSABLE_MEAL_MULTIPLE_PURCHASE;
          }

          break;
        case MEAL_TYPES_LABELS.Other:
          const numReimbursableSnack = pendingItems.reduce(
            (sum, item) => sum + (isReimbursableSnack(item) ? item.Qty : 0),
            0
          );

          if (BoughtReimbSnack && numReimbursableSnack) {
            notificationObj =
              NOTIFICATION_DIALOG_MESSAGES.REIMBURSABLE_SNACK_PURCHASED;
          } else if (numReimbursableSnack > 1) {
            notificationObj =
              NOTIFICATION_DIALOG_MESSAGES.REIMBURSABLE_SNACK_MULTIPLE_PURCHASE;
          }

          break;
        default:
      }
    }
  }

  return notificationObj;
};

export const sortByLastNameFirstName = persons =>
  _.sortBy(persons, ['LastName', 'FirstName']);

export const getItemPricingForGrade = (item, gradeId) => {
  let PricingForGrade = item.Pricing.find(p => p.GradeId === gradeId);

  if (!PricingForGrade) {
    PricingForGrade = item.Pricing.find(p => p.GradeId === DEFAULT_GRADE_ID);
  }
  return PricingForGrade;
};

/**
 *
 * @param {Object} pricing
 * @param {Number} pricing.FreePrice
 * @param {Number} pricing.ReducedPrice
 * @param {Number} pricing.StudentPrice
 * @param {Number} pricing.TeacherPrice
 * @param {Number} pricing.AdultPrice
 * @param {Number} pricing.CateredPrice
 * @param {Number} pricing.SFSPrice
 * @param {Number} status
 * @returns {Number}
 */
export const getPriceForStatus = (pricing, status) =>
  pricing[StatusPriceMap[status]];

export const getItemConvertAmountForGrade = (item, gradeId) => {
  const ConvertAmountForGrade = item.ConvertAmounts.find(
    ca => ca.GradeId === gradeId
  );
  let Converted = false;
  let ConvertAmount = null;
  if (ConvertAmountForGrade) {
    Converted = ConvertAmountForGrade.Converted;
    ConvertAmount = ConvertAmountForGrade.Amount;
  }
  return {
    Converted,
    ConvertAmount,
  };
};

/**
 * Checks if the account is a generic account
 * @param {Object} account
 * @param {String} account.Id
 * @returns {Boolean}
 */
export const isGenericAccount = ({ Id }) => {
  return GENERIC_ACCOUNT_IDS.has(Id);
};

export const formatServedItemResponse = (acc, curr) => {
  const ItemCategoryId = curr.ItemCategoryId;
  if (_.isUndefined(acc[ItemCategoryId])) {
    acc[ItemCategoryId] = [];
  }
  acc[curr.ItemCategoryId].push(curr);
  curr.selected = !_.isNil(curr.OrderNum);
  return acc;
};

export const formatServedItems = servedItems => {
  const Breakfast = servedItems.filter(
    item => item.ItemId[0] === MealTypesEnum.Breakfast
  );
  const Lunch = servedItems.filter(
    item => item.ItemId[0] === MealTypesEnum.Lunch
  );
  const Supper = servedItems.filter(
    item => item.ItemId[0] === MealTypesEnum.Supper
  );
  const Other = servedItems.filter(
    item => item.ItemId[0] === MealTypesEnum.Other
  );

  /* UI demands this format */
  const foodItems = {
    Breakfast: Breakfast.reduce(formatServedItemResponse, {}),
    Lunch: Lunch.reduce(formatServedItemResponse, {}),
    Supper: Supper.reduce(formatServedItemResponse, {}),
    Other: Other.reduce(formatServedItemResponse, {}),
  };
  return foodItems;
};

export const formatFoodItems = foodItems => {
  return _.flattenDeep(
    Object.values(foodItems).map(mealType => Object.values(mealType))
  );
};

export const reorderList = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

/**
 *
 * @param {Array<Object>} servedItems
 */
export const extractServedItemOrder = servedItems => {
  const servedItemsOrder = {
    [MealTypesEnum.Breakfast]: [],
    [MealTypesEnum.Lunch]: [],
    [MealTypesEnum.Supper]: [],
    [MealTypesEnum.Other]: [],
  };

  _(servedItems)
    .filter(i => !_.isNil(i.OrderNum))
    .orderBy('OrderNum')
    .forEach(servedItem => {
      const ItemId = _.get(servedItem, 'ItemId', '');
      const mealType = ItemId[0];
      if (mealType) {
        servedItemsOrder[mealType].push(ItemId);
      }
    });

  return servedItemsOrder;
};

export const parseServedItemOrder = servedItemOrder => {
  return _.flattenDeep(
    Object.entries(servedItemOrder).map(([mealType, itemIds]) => {
      return itemIds.map((itemId, index) => ({
        ItemId: itemId,
        OrderNum: index,
      }));
    })
  );
};



// WEBPACK FOOTER //
// ./src/pages/pointofsale/modules/common/helpers/index.js